// ADR-0146 — el centro de notificaciones del propietario (F13, fase 2
// corte 3, ticket #0741).
//
//   GET  /me/notifications              → la lista (paginada; filtros
//                                         opcionales assetId y category)
//   GET  /me/notifications/unread-count → el punto de la campana
//   POST /me/notifications/:id/read     → marca leído un aviso (204)
//   POST /me/notifications/read-all     → «Marcar todo leído»
//
// Sólo para la contraparte EXTERNA del asset (el propietario de
// postventa): otro rol recibe 403 NOTIFICATIONS_ACTOR_NOT_ALLOWED. Todo se
// acota a las unidades que el usuario posee HOY: si una unidad cambia de
// dueño, el anterior deja de ver sus avisos (y «Marcar todo leído» no los
// toca).
//
// El servidor arma título, cuerpo y acción desde la entidad; la fila sólo
// guarda ids. El cuerpo nunca lleva el texto que escribió la inmobiliaria
// (ni la respuesta a una consulta, ni el motivo de un rechazo): eso se lee
// en el hilo. La única excepción son los avisos y las guías de mantención
// por proyecto (ADR-0152): su título y su cuerpo SON el texto de la
// inmobiliaria, completo, y no hay hilo donde leerlo.
import { ownerNotificationCategorySchema, ownerNotificationChannelSchema, ownerNotificationKindSchema, ownerSurveyKindSchema, } from "@bitacorapro/core-engine";
import { z } from "zod";
import { ownerSurveyResponseSchema, ownerSurveyScaleSchema } from "./owner-surveys.js";
export const NOTIFICATIONS_ACTOR_NOT_ALLOWED = "NOTIFICATIONS_ACTOR_NOT_ALLOWED";
/** Página por defecto y tope de la lista. */
export const OWNER_NOTIFICATIONS_PAGE_SIZE = 30;
export const OWNER_NOTIFICATIONS_PAGE_MAX = 50;
/** Etiquetas visibles de cada categoría: el chip (plural) y la línea de
 *  meta de cada aviso (singular), como en el handoff (`NCAT` / `CHIP`).
 *  Ninguna app las escribe como literal. */
export const OWNER_NOTIFICATION_CATEGORY_LABEL = {
    obs: { chip: "Observaciones", meta: "Observación" },
    consulta: { chip: "Consultas", meta: "Consulta" },
    aviso: { chip: "Avisos", meta: "Aviso" },
    mant: { chip: "Mantención", meta: "Mantención" },
    doc: { chip: "Documentos", meta: "Documento" },
    entrega: { chip: "Entrega", meta: "Entrega" },
    enc: { chip: "Encuestas", meta: "Encuesta" },
};
export const ownerNotificationActionSchema = z.object({
    label: z.string(),
    /** Ruta de la PWA (sin origen): el hilo de la observación o de la
     *  consulta. */
    href: z.string(),
});
/** ADR-0152 — lo propio de un aviso o una guía por proyecto. */
export const ownerNotificationAnnouncementSchema = z.object({
    /** Retirado por la inmobiliaria: la PWA lo pinta tachado con la
     *  etiqueta «Retirado». El texto sigue visible (nada se borra). */
    retracted: z.boolean(),
    /** El PDF adjunto, si lo hay y el aviso no está retirado. Se abre con
     *  GET /me/notifications/:id/attachment-url. */
    attachment: z.object({ sizeBytes: z.number().int().positive() }).nullable(),
});
/** ADR-0153 — lo propio de una encuesta: con qué escala se responde y,
 *  si ya respondió, qué. La PWA pinta la escala del NPS dentro de la
 *  notificación; la de la visita se responde en su pantalla. */
export const ownerNotificationSurveySchema = z.object({
    id: z.string().uuid(),
    kind: ownerSurveyKindSchema,
    scale: ownerSurveyScaleSchema,
    response: ownerSurveyResponseSchema.nullable(),
    /** La pantalla de la encuesta (el comentario opcional, y la escala de la
     *  visita). El NPS no lleva `action`: se responde en la fila. */
    href: z.string(),
});
export const ownerNotificationSchema = z.object({
    id: z.string().uuid(),
    category: ownerNotificationCategorySchema,
    kind: ownerNotificationKindSchema,
    assetId: z.string().uuid().nullable(),
    /** «{Proyecto} · Unidad {X}» (`propiedadLabel`). */
    propiedadLabel: z.string().nullable(),
    title: z.string(),
    body: z.string(),
    /** null cuando no hay a dónde llevar (p. ej. una unidad sin proyecto). */
    action: ownerNotificationActionSchema.nullable(),
    createdAt: z.string(),
    readAt: z.string().nullable(),
    /** Sólo en los avisos por proyecto (ADR-0152); ausente en el resto. */
    announcement: ownerNotificationAnnouncementSchema.nullable().optional(),
    /** Sólo en las encuestas (ADR-0153); ausente en el resto. */
    survey: ownerNotificationSurveySchema.nullable().optional(),
});
export const ownerNotificationListQuerySchema = z.object({
    assetId: z.string().uuid().optional(),
    category: ownerNotificationCategorySchema.optional(),
    /** El `nextCursor` de la página anterior (opaco). */
    cursor: z.string().max(200).optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(OWNER_NOTIFICATIONS_PAGE_MAX)
        .default(OWNER_NOTIFICATIONS_PAGE_SIZE),
});
export const ownerNotificationListResponseSchema = z.object({
    notifications: z.array(ownerNotificationSchema),
    nextCursor: z.string().nullable(),
    /** No leídos de TODAS sus unidades actuales, sin filtros: gobierna
     *  «Marcar todo leído» (que tampoco mira los filtros). */
    unreadCount: z.number().int().nonnegative(),
    /** F9 (corte 8) — las categorías con al menos un aviso visible, sin
     *  filtros: la PWA muestra el chip de una categoría que no siempre
     *  aplica (hoy, «Entrega») sólo si tiene avisos. Opcional por
     *  compatibilidad con un core-api anterior. */
    categories: z.array(ownerNotificationCategorySchema).optional(),
});
export const ownerNotificationUnreadCountResponseSchema = z.object({
    unreadCount: z.number().int().nonnegative(),
});
export const ownerNotificationReadAllResponseSchema = z.object({
    /** Cuántos avisos pasaron a leídos. */
    updated: z.number().int().nonnegative(),
});
// ─── preferencias (ADR-0147, fase 2 corte 4, #0742) ──────────────────
//
//   GET /me/notification-preferences → las categorías visibles, el estado
//                                      de cada canal y lo que siempre llega
//   PUT /me/notification-preferences → cambia UN (categoría, canal);
//                                      idempotente; devuelve el estado nuevo
//
// Mismo alcance de rol que /me/notifications (403
// NOTIFICATIONS_ACTOR_NOT_ALLOWED). Por defecto todo está encendido; sólo
// se guardan las exclusiones. Apagar «Correo» suprime el correo opcional
// de esa categoría en core-api, antes de pedírselo a auth-svc; apagar «En
// la app» no borra avisos: la lista y el conteo los ocultan, y volver a
// encender los muestra de nuevo. Los avisos bloqueados (por tipo,
// `OWNER_NOTICE_LOCK` del engine) llegan siempre, por los dos canales.
/** El texto de cada aviso en la pantalla de preferencias: una frase corta
 *  y, para los que no se pueden apagar, por qué. Dos tipos con la misma
 *  frase (los dos recordatorios del cierre) se muestran una sola vez. */
export const OWNER_NOTICE_COPY = {
    observation_ready_for_review: {
        label: "Observación lista para tu revisión",
        reason: "Desde ese día corre el plazo para que nos confirmes si quedó bien.",
    },
    observation_assigned: { label: "Observación con contratista asignado", reason: null },
    observation_accepted: { label: "Observación aceptada", reason: null },
    observation_rejected: {
        label: "Observación rechazada, con su motivo",
        reason: "Es una decisión sobre tu solicitud y te permite apelar.",
    },
    observation_appeal_resolved: {
        label: "Respuesta a tu apelación",
        reason: "Es la decisión final sobre tu solicitud.",
    },
    observation_closure_reminder: {
        label: "Recordatorios antes de cerrar una observación por falta de respuesta",
        reason: "Si no respondes, la observación se cierra: necesitas saberlo a tiempo.",
    },
    observation_closure_last_reminder: {
        label: "Recordatorios antes de cerrar una observación por falta de respuesta",
        reason: "Si no respondes, la observación se cierra: necesitas saberlo a tiempo.",
    },
    observation_auto_closed: {
        label: "Cierre de una observación por falta de respuesta",
        reason: "Te dice hasta cuándo puedes reabrirla.",
    },
    inquiry_reply: { label: "Respuesta a tu consulta", reason: null },
    observation_visit_scheduled: { label: "Visita agendada o con nueva fecha", reason: null },
    delivery_scheduled: { label: "Fecha de entrega fijada o cambiada", reason: null },
    announcement_published: { label: "Avisos de tu proyecto", reason: null },
    maintenance_guide_published: { label: "Guías de mantención de tu proyecto", reason: null },
    survey_nps: { label: "Encuesta sobre tu experiencia con la entrega", reason: null },
    survey_visit: { label: "Encuesta sobre la visita técnica", reason: null },
    inquiry_converted: {
        label: "Tu consulta pasó a ser una observación",
        reason: "Desde ese día tu consulta se sigue en la observación.",
    },
    inquiry_receipt: {
        label: "Confirmación por correo de que recibimos tu consulta",
        reason: "Te dice la fecha en que te responderemos.",
    },
    // ADR-0161 (#0768).
    // Neutral: la confirmación automática depende del job y de la activación
    // de la org, y esta pantalla no lo sabe (el correo y el hilo sí lo dicen).
    observation_visit_proposed: {
        label: "Visita por confirmar propuesta por el contratista",
        reason: "Te pide confirmar el día y la hora, o pedir otro horario.",
    },
    observation_visit_auto_confirmed: {
        label: "Visita confirmada sin tu respuesta",
        reason: "Solo en la app: te dice que la visita quedó confirmada porque no respondiste a tiempo.",
    },
};
export const ownerNotificationAlwaysOnSchema = z.object({
    label: z.string(),
    reason: z.string(),
});
export const ownerNotificationPreferenceCategorySchema = z.object({
    category: ownerNotificationCategorySchema,
    /** «Observaciones», «Consultas» (`OWNER_NOTIFICATION_CATEGORY_LABEL`). */
    label: z.string(),
    /** Lo que el interruptor gobierna: los avisos opcionales de la
     *  categoría, en frases cortas. */
    optional: z.array(z.string()),
    email: z.boolean(),
    app: z.boolean(),
    /** «Siempre te avisaremos de: …» — los bloqueados, con su motivo. */
    alwaysOn: z.array(ownerNotificationAlwaysOnSchema),
});
export const ownerNotificationPreferencesResponseSchema = z.object({
    categories: z.array(ownerNotificationPreferenceCategorySchema),
});
export const ownerNotificationPreferenceUpdateSchema = z
    .object({
    category: ownerNotificationCategorySchema,
    channel: ownerNotificationChannelSchema,
    enabled: z.boolean(),
})
    .strict();
/** 400 — una categoría que hoy no se ofrece (sin escritor). */
export const NOTIFICATION_CATEGORY_NOT_CONFIGURABLE = "NOTIFICATION_CATEGORY_NOT_CONFIGURABLE";
// ADR-0147 (corte de ajustes, founder 2026-10-16) — qué pasó con el correo
// al propietario tras un acto del personal. El panel ya no dice «se
// notificó» cuando el propietario apagó ese correo en sus preferencias:
// - `sent`: auth-svc confirmó el envío;
// - `suppressed_by_preference`: el propietario eligió no recibir este aviso
//   por correo (lo sigue viendo en la campana de la app). No es un fallo;
// - `failed`: no salió — sin propietario activado, auth-svc lo saltó, el
//   envío falló o no se sabe (timeout).
// Viaja como `ownerNotice` junto a `ownerNotified` (que sigue igual para
// los clientes desplegados: true con `sent` o `suppressed_by_preference`).
// Ausente cuando el acto no le escribe al propietario.
export const OWNER_NOTICE_OUTCOMES = ["sent", "suppressed_by_preference", "failed"];
export const ownerNoticeOutcomeSchema = z.enum(OWNER_NOTICE_OUTCOMES);
/** Varios envíos de un mismo acto (varios propietarios o unidades): con
 *  uno que salió, `sent`; si no, con uno apagado por preferencia,
 *  `suppressed_by_preference`; si no, `failed`. Sin ninguno, null. */
export function combineOwnerNoticeOutcomes(outcomes) {
    if (outcomes.length === 0)
        return null;
    if (outcomes.includes("sent"))
        return "sent";
    if (outcomes.includes("suppressed_by_preference"))
        return "suppressed_by_preference";
    return "failed";
}
// ADR-0162 §7 — POST /me/notifications/:id/read. `fromPush`: el aviso se
// tocó en el teléfono, donde UNA notificación representa todos los avisos
// de la misma cosa (el tag reemplaza los anteriores y el drain agrupa los
// de una pasada): se marcan leídos con él los anteriores sin leer de esa
// misma entidad que suenan en el teléfono. Sin él (la campana), sólo ese.
export const ownerNotificationReadBodySchema = z.object({ fromPush: z.boolean().optional() });
//# sourceMappingURL=owner-notifications.js.map